import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { subscriptionAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Crown,
  Check,
  Loader2,
  Building,
  Home,
  Users,
  Headphones,
  Zap,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  AlertCircle,
  X,
  CreditCard,
} from 'lucide-react';

import { useI18n } from '../../context/I18nContext';

const Subscription = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [myStatus, setMyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingTier, setProcessingTier] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    loadPlans();
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('subscription_success');
    const cancelled = searchParams.get('plan_change_cancelled');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else if (success === 'true') {
      toast.success('Subscription activated successfully!');
      window.history.replaceState({}, '', '/landlord/subscription');
      loadPlans();
    } else if (cancelled === 'true') {
      toast.info('Plan change cancelled. Your current plan remains active.');
      window.history.replaceState({}, '', '/landlord/subscription');
    }
  }, [searchParams]);

  const loadPlans = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        subscriptionAPI.getPlans(),
        subscriptionAPI.getMyStatus(),
      ]);
      setPlans(plansRes.data);
      setMyStatus(statusRes.data);
    } catch (error) {
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    const poll = async () => {
      try {
        const response = await subscriptionAPI.getStatus(sessionId);
        if (response.data.payment_status === 'paid' || response.data.status === 'complete') {
          toast.success('Subscription activated successfully!');
          window.history.replaceState({}, '', '/landlord/subscription');
          loadPlans();
          setCheckingPayment(false);
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired');
          setCheckingPayment(false);
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Payment is being processed. Please refresh.');
          setCheckingPayment(false);
        }
      } catch (error) {
        setCheckingPayment(false);
      }
    };
    poll();
  };

  const handlePlanClick = async (tier) => {
    const currentTier = myStatus?.tier || 'NONE';
    if (currentTier === tier) return;

    // If user has NO plan yet, go straight to checkout (no change preview needed)
    if (currentTier === 'NONE' || !myStatus?.is_active) {
      setProcessingTier(tier);
      try {
        const params = new URLSearchParams({ tier, origin_url: window.location.origin });
        const res = await subscriptionAPI.checkout(tier, window.location.origin);
        if (res.data.url) {
          window.location.href = res.data.url;
          return;
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to start checkout');
      } finally {
        setProcessingTier(null);
      }
      return;
    }

    // Existing subscriber: show preview/confirmation dialog
    setPreviewLoading(tier);
    try {
      const response = await subscriptionAPI.previewChange(tier);
      const preview = response.data;
      setConfirmDialog({ tier, preview });
    } catch (error) {
      toast.error('Failed to preview plan change');
    } finally {
      setPreviewLoading(null);
    }
  };

  const handleConfirmChange = async () => {
    if (!confirmDialog) return;
    const { tier, preview } = confirmDialog;
    setConfirming(true);

    try {
      // All plan changes go through Stripe Checkout
      const response = await subscriptionAPI.changePlan(tier);
      
      if (response.data.url) {
        // Redirect to Stripe Checkout for payment
        window.location.href = response.data.url;
        return;
      } else if (response.data.success && !response.data.requires_checkout) {
        toast.success(response.data.message || `Plan changed to ${tier}!`);
        loadPlans();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change plan';
      toast.error(message);
    } finally {
      setConfirming(false);
      setConfirmDialog(null);
    }
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'BASIC': return Home;
      case 'PRO': return Building;
      case 'EXPERT': return Users;
      case 'ENTERPRISE': return Crown;
      default: return Building;
    }
  };

  const getPlanTierIndex = (tier) => {
    const tierOrder = ['BASIC', 'PRO', 'EXPERT', 'ENTERPRISE'];
    return tierOrder.indexOf(tier);
  };

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('subscription.title')}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </DashboardLayout>
    );
  }

  const currentTierIndex = getPlanTierIndex(myStatus?.tier || 'NONE');
  const hasNoPlan = !myStatus?.is_active && (myStatus?.tier === 'NONE' || myStatus?.status === 'none');

  return (
    <DashboardLayout type="landlord" title={t('subscription.title')}>
      <div className="space-y-8" data-testid="subscription-page">
        {checkingPayment && (
          <Card className="bg-gold/10 border-gold/30">
            <CardContent className="p-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-gold" />
              <span>{t('subscription.processing')}</span>
            </CardContent>
          </Card>
        )}

        {/* Trial Banner */}
        {myStatus?.status === 'trialing' && (
          <Card className="bg-[#00a99d]/10 border-[#00a99d]/30" data-testid="trial-banner">
            <CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#00a99d]/15 flex items-center justify-center rounded-full">
                  <Zap className="w-6 h-6 text-[#00a99d]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Free Trial Active</h3>
                  <p className="text-sm text-muted-foreground">
                    {myStatus.trial_days_remaining != null
                      ? `${myStatus.trial_days_remaining} day${myStatus.trial_days_remaining !== 1 ? 's' : ''} remaining — your card will be charged after the trial ends`
                      : 'Your free trial is active. You will be charged when it ends.'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await subscriptionAPI.getPortal();
                    window.open(res.data.url, '_blank');
                  } catch {
                    toast.error('Unable to open billing portal');
                  }
                }}
                className="border-[#00a99d]/30 text-[#00a99d] hover:bg-[#00a99d]/10"
                data-testid="trial-manage-billing-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Manage Billing
              </Button>
            </CardContent>
          </Card>
        )}

        {/* No Plan Banner */}
        {hasNoPlan && (
          <Card className="bg-amber-500/10 border-amber-500/30" data-testid="no-plan-banner">
            <CardContent className="p-6 text-center space-y-3">
              <div className="w-14 h-14 mx-auto bg-gold/10 flex items-center justify-center rounded-full">
                <Crown className="w-7 h-7 text-gold" />
              </div>
              <h2 className="font-serif text-2xl">Choose a plan to get started</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                You need an active subscription to manage properties, units, tenants, and payments. Select a plan below to begin.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Current Plan & Usage — only show if they have an active plan */}
        {!hasNoPlan && (
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('subscription.currentPlan')}</p>
                  <h3 className="text-xl font-serif">
                    {myStatus?.tier}{' '}
                    <span className="text-sm ml-2 px-2 py-0.5 bg-gold/10 text-gold rounded">
                      {myStatus?.status}
                    </span>
                  </h3>
                </div>
              </div>
              {myStatus?.usage && (
                <div className="flex gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{myStatus.usage.properties}</p>
                    <p className="text-xs text-muted-foreground">
                      / {myStatus.limits?.properties === -1 ? '\u221e' : myStatus.limits?.properties} Properties
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{myStatus.usage.units}</p>
                    <p className="text-xs text-muted-foreground">
                      / {myStatus.limits?.units === -1 ? '\u221e' : myStatus.limits?.units} Units
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await subscriptionAPI.getPortal();
                      window.open(res.data.url, '_blank');
                    } catch {
                      toast.error('Unable to open billing portal');
                    }
                  }}
                  className="border-gold/30 text-gold hover:bg-gold/10"
                  data-testid="manage-billing-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Manage Billing
                </Button>
                <Button variant="outline" size="sm" onClick={loadPlans}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => {
            const PlanIcon = getPlanIcon(plan.tier);
            const isCurrentPlan = myStatus?.is_active && myStatus?.tier === plan.tier;
            const planTierIndex = getPlanTierIndex(plan.tier);
            const isUpgrade = planTierIndex > currentTierIndex;
            const isDowngrade = planTierIndex < currentTierIndex;
            const isPopular = plan.tier === 'PRO';

            return (
              <Card
                key={plan.tier}
                className={`bg-card border-border relative overflow-hidden transition-all duration-300 ${
                  isCurrentPlan
                    ? 'border-gold ring-2 ring-gold/20'
                    : isPopular
                    ? 'border-gold/50'
                    : 'hover:border-gold/30'
                } card-animate`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`plan-card-${plan.tier.toLowerCase()}`}
              >
                {isPopular && !isCurrentPlan && (
                  <div className="absolute top-0 right-0 bg-gold text-white text-xs font-bold px-3 py-1">
                    POPULAR
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-3 py-1">
                    CURRENT
                  </div>
                )}
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4">
                    <PlanIcon className="w-6 h-6 text-gold" />
                  </div>
                  <CardTitle className="font-serif text-xl">{plan.name}</CardTitle>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">${plan.price}</span>
                    <span className="text-muted-foreground text-sm">/month</span>
                    {!myStatus?.is_active && (
                      <div className="text-xs text-[#00a99d] font-semibold mt-1">7-day free trial</div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Properties:</span>
                      <span className="font-medium">
                        {plan.limits?.properties === -1 ? 'Unlimited' : plan.limits?.properties}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-muted-foreground">Units:</span>
                      <span className="font-medium">
                        {plan.limits?.units === -1 ? 'Unlimited' : plan.limits?.units}
                      </span>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${
                      isCurrentPlan
                        ? 'bg-muted text-muted-foreground cursor-not-allowed'
                        : hasNoPlan || isUpgrade
                        ? 'bg-gold text-white hover:bg-gold-light'
                        : 'bg-muted/50 text-foreground hover:bg-muted'
                    }`}
                    onClick={() => !isCurrentPlan && handlePlanClick(plan.tier)}
                    disabled={isCurrentPlan || !!previewLoading || !!confirming || !!processingTier}
                    data-testid={`plan-${plan.tier.toLowerCase()}-btn`}
                  >
                    {(previewLoading === plan.tier || processingTier === plan.tier) ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : !hasNoPlan && isUpgrade ? (
                      <ArrowUp className="w-4 h-4 mr-2" />
                    ) : !hasNoPlan && isDowngrade ? (
                      <ArrowDown className="w-4 h-4 mr-2" />
                    ) : null}
                    {isCurrentPlan ? 'Current Plan' : hasNoPlan ? 'Start Free Trial' : isUpgrade ? 'Upgrade' : 'Downgrade'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Plan Changes Info */}
        <Card className="bg-muted/20 border-border">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">{t('subscription.planChanges')}</p>
              <p>
                {t('subscription.planChangesDesc')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Why Upgrade */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-serif">{t('subscription.whyUpgrade')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Building className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <h4 className="font-medium mb-1">{t('subscription.moreProperties')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('subscription.morePropertiesDesc')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <h4 className="font-medium mb-1">{t('subscription.advancedFeatures')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('subscription.advancedFeaturesDesc')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <h4 className="font-medium mb-1">{t('subscription.prioritySupport')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('subscription.prioritySupportDesc')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="confirm-plan-dialog">
          <div className="bg-card border border-border rounded-lg shadow-2xl max-w-md w-full mx-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-serif font-medium">Confirm Plan Change</h3>
                <button
                  onClick={() => setConfirmDialog(null)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="close-confirm-dialog"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Plan comparison */}
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Current</p>
                    <p className="font-bold">{confirmDialog.preview.current_tier}</p>
                    <p className="text-sm text-muted-foreground">${confirmDialog.preview.current_monthly_price}/mo</p>
                  </div>
                  <ArrowUp className={`w-5 h-5 ${confirmDialog.preview.is_upgrade ? 'text-gold' : 'text-muted-foreground rotate-180'}`} />
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">New</p>
                    <p className="font-bold text-gold">{confirmDialog.preview.new_tier}</p>
                    <p className="text-sm text-muted-foreground">${confirmDialog.preview.new_monthly_price}/mo</p>
                  </div>
                </div>

                {/* Charge/Credit info */}
                <div className="p-4 rounded-lg border bg-gold/5 border-gold/20">
                  <div className="flex items-start gap-3">
                    <CreditCard className="w-5 h-5 mt-0.5 shrink-0 text-gold" />
                    <div>
                      <p className="font-medium text-gold">
                        {confirmDialog.preview.is_upgrade ? 'Payment Required' : 'Plan Change'}:
                        ${confirmDialog.preview.new_monthly_price}/month
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {confirmDialog.preview.message}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setConfirmDialog(null)}
                  disabled={confirming}
                  data-testid="cancel-plan-change"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gold text-white hover:bg-gold-light"
                  onClick={handleConfirmChange}
                  disabled={confirming}
                  data-testid="confirm-plan-change-btn"
                >
                  {confirming ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  {confirming
                    ? 'Redirecting to Stripe...'
                    : confirmDialog.preview.is_upgrade
                    ? 'Proceed to Payment'
                    : 'Proceed to Payment'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Subscription;
